import { APP_WIDGET_INPUT_MODE } from "@/constants";

export const APP_CHART_GRID_COLUMNS = 12;

/** Active input mode in the Add/Edit chart, table, and KPI card dialogs (Query, Form, or Prompt). */
export type AppWidgetInputMode = (typeof APP_WIDGET_INPUT_MODE)[keyof typeof APP_WIDGET_INPUT_MODE];

/**
 * Default column span for a newly added KPI card (3 per row). Must be one of
 * AppChartColumnSpanSelect's block values (steps of 2) — that picker is
 * shared with charts/tables, and a span outside its representable set gets
 * silently rounded the moment the picker is opened, causing same-size KPI
 * cards to drift to different widths.
 */
export const APP_KPI_DEFAULT_COLUMN_SPAN = 4;

/** Divisors of 12 that produce clean grid layouts. */
export const APP_CHART_COLUMN_SPAN_OPTIONS = [12, 6, 4, 3, 2, 1] as const;

export type AppChartColumnSpan = (typeof APP_CHART_COLUMN_SPAN_OPTIONS)[number];

export function normalizeColumnSpan(value: number | undefined): number {
  if (typeof value !== "number" || !Number.isInteger(value)) {
    return APP_CHART_GRID_COLUMNS;
  }
  return Math.min(Math.max(value, 1), APP_CHART_GRID_COLUMNS);
}

export type StackableLayoutItem = {
  kind: "chart" | "table" | "kpi" | "nugget_feed" | "text";
  columnSpan: number;
};

export type StackedLayoutGroup<T extends StackableLayoutItem> =
  | { kind: "single"; item: T }
  | { kind: "kpiPair"; items: [T, T] };

/**
 * Groups a sort-ordered item list for rendering: a KPI card is exactly half
 * the height of a chart/table row, so two adjacent KPI items of the same
 * column span are paired into a single stacked cell that matches a
 * chart/table row's height. Everything else (charts, tables, unpaired KPIs)
 * renders as its own single-item cell. Pairing never changes the underlying
 * sort order — it only decides how consecutive items are grouped visually.
 */
export function groupItemsForStackedLayout<T extends StackableLayoutItem>(
  items: readonly T[],
): StackedLayoutGroup<T>[] {
  const groups: StackedLayoutGroup<T>[] = [];
  let index = 0;
  while (index < items.length) {
    const current = items[index];
    const next = items[index + 1];
    const canPairWithNext =
      current.kind === "kpi" && next?.kind === "kpi" && current.columnSpan === next.columnSpan;
    if (canPairWithNext) {
      groups.push({ kind: "kpiPair", items: [current, next] });
      index += 2;
    } else {
      groups.push({ kind: "single", item: current });
      index += 1;
    }
  }
  return groups;
}
